import type {
  AnalyticsPayload,
  AppUser,
  AuditLog,
  Campaign,
  CurrentUser,
  DataSource,
  DataSourcePage,
  DataSourceOutcomeSummary,
  DashboardStats,
  EnrichmentWorkspaceSummary,
  Job,
  ExtractedLeadDraft,
  Lead,
  LeadDataType,
  LeadDetailsUpdate,
  LeadFilters,
  LeadPage,
  LeadTimeline,
  PerformanceSnapshot,
  RecoverableStatusChange,
  StatusUpdateBatch,
  StatusUpdateBatchDetail,
  LeadStatus,
  MailLogSource,
  FollowUpTask,
  FollowUpTaskStatus,
  FollowUpSummary,
  PasteImportResult,
  MediaExtractionPreview,
  PastePreviewResult,
  SuppressedEmail,
  UserRole
} from './types';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';
const tokenKey = 'email-data-platform-token';

export function getAuthToken() {
  return window.localStorage.getItem(tokenKey);
}

export function setAuthToken(token: string | null) {
  if (token) {
    window.localStorage.setItem(tokenKey, token);
  } else {
    window.localStorage.removeItem(tokenKey);
  }
}

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    search.set(key, String(value));
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}

function withAuth(init?: RequestInit): RequestInit {
  const token = getAuthToken();
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return {
    ...init,
    headers
  };
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, withAuth(init));
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.message ?? `Request failed with ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function login(email: string, password: string) {
  const session = await request<{
    token: string;
    expiresAt: string;
    user: CurrentUser;
  }>('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  setAuthToken(session.token);
  return session;
}

export function getMe() {
  return request<{ user: CurrentUser }>('/api/auth/me');
}

export async function logout() {
  await request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });
  setAuthToken(null);
}

export async function changePassword(currentPassword: string, nextPassword: string) {
  await request<{ ok: boolean }>('/api/auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, nextPassword })
  });
  setAuthToken(null);
}

export function listLeads(filters: LeadFilters, cursor?: string, signal?: AbortSignal) {
  return request<LeadPage>(
    `/api/leads${buildQuery({
      ...filters,
      includeTotal: false,
      cursor,
      status: filters.status || undefined,
      preset: filters.preset || undefined
    })}`, { signal }
  );
}

export async function countLeads(filters: LeadFilters, signal?: AbortSignal) {
  const url = `/api/leads/count${buildQuery({ ...filters, cursor: undefined, async: true })}`;
  while (!signal?.aborted) {
    const result = await request<{ state?: string; totalCount: number | null; error?: string }>(url, { signal });
    if (result.totalCount !== null) return { totalCount: result.totalCount };
    if (result.state !== 'pending') throw new Error(result.error || 'Result count unavailable. Please retry.');
    await new Promise<void>((resolve, reject) => {
      const abort = () => { clearTimeout(timer); signal?.removeEventListener('abort', abort); reject(new DOMException('Aborted', 'AbortError')); };
      const timer = window.setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, 1500);
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) abort();
    });
  }
  throw new DOMException('Aborted', 'AbortError');
}

export function getLeadTimeline(id: string) {
  return request<LeadTimeline>(`/api/leads/${id}/timeline`);
}

export function listRecoverableStatusChanges() {
  return request<RecoverableStatusChange[]>('/api/leads/status-recovery/candidates');
}

export function restoreRecoverableStatusChanges(eventIds: string[]) {
  return request<{ restored: number }>('/api/leads/status-recovery/restore', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eventIds })
  });
}

export function listStatusUpdateBatches() {
  return request<{ rows: StatusUpdateBatch[] }>('/api/imports/status-batches');
}

export function getStatusUpdateBatch(id: string) {
  return request<StatusUpdateBatchDetail>(`/api/imports/status-batches/${id}`);
}

export function getStats() {
  return request<DashboardStats>('/api/stats');
}

export function listJobHistory() {
  return request<{ rows: Job[] }>('/api/jobs/history');
}

export function getEnrichmentWorkspaceSummary() {
  return request<EnrichmentWorkspaceSummary>('/api/jobs/enrichment-summary');
}

export function getAnalytics() {
  return request<AnalyticsPayload>('/api/analytics');
}

export function getPerformanceSnapshot() {
  return request<PerformanceSnapshot>('/api/performance');
}

export function listDataSources(options: {
  includeCounts?: boolean;
  cursor?: string;
  limit?: number;
  forceReplyCounts?: boolean;
  query?: string;
  kind?: 'file' | 'paste' | 'manual';
  reply?: 'positive' | 'negative' | 'both' | 'no_positive' | 'no_negative';
} = {}) {
  return request<DataSourcePage>(`/api/sources${buildQuery(options)}`);
}

export function getDataSourceSummary(payload: { sourceJobId?: string; sourceFile?: string; sourceUploadDate?: string; sourceOwnerId?: string; sourceKind?: 'file' | 'paste' | 'manual' }) {
  return request<DataSourceOutcomeSummary>(`/api/sources/summary${buildQuery(payload)}`);
}

export function enrichDataSource(payload: { sourceJobId?: string; sourceFile?: string; sourceUploadDate?: string; sourceOwnerId?: string; sourceKind?: 'file' | 'paste' | 'manual' }) {
  return request<Job>('/api/sources/enrich', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
  });
}

export function enrichWorkspaceMissingDetails() {
  return request<Job>('/api/sources/enrich-missing', { method: 'POST' });
}

export function verifyRetailIndustries() {
  return request<Job>('/api/sources/enrich-retail-industries', { method: 'POST' });
}

export function listFollowUps() {
  return request<{ rows: FollowUpTask[]; summary: FollowUpSummary }>('/api/follow-ups');
}

export function listMailLogSources() {
  return request<{ rows: MailLogSource[] }>('/api/mail-log-sources');
}

export function createMailLogSource(payload: {
  name: string;
  portalUrl: string;
  intervalMinutes: number;
  lookbackDays: number;
  allowInsecureTls: boolean;
}) {
  return request<MailLogSource>('/api/mail-log-sources', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
  });
}

export async function syncMailLogSource(id: string, options: { force?: boolean; allHistory?: boolean; includeLogs?: boolean } = {}) {
  const result = await request<{ processedFiles: number; updatedRows: number } | {queued:true;jobId:string}>(`/api/mail-log-sources/${id}/sync`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(options)
  });
  if (!('queued' in result)) return result;
  for (;;) {
    await new Promise(resolve=>setTimeout(resolve,3000));
    const job=await getJob(result.jobId);
    if (job.status==='failed') throw new Error(job.errorMessage || 'Server sync failed; inspect the job for details.');
    if (job.status==='completed') return job.options.syncResult as {processedFiles:number;updatedRows:number};
  }
}

export function updateMailLogSource(id: string, payload: {
  name: string; portalUrl: string; intervalMinutes: number; lookbackDays: number; allowInsecureTls: boolean;
}) {
  return request<MailLogSource>(`/api/mail-log-sources/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
}

export function deleteMailLogSource(id: string) {
  return request<void>(`/api/mail-log-sources/${id}`, { method: 'DELETE' });
}

export function scheduleFollowUps(payload: {
  sourceJobId?: string;
  sourceFile?: string;
  sourceUploadDate?: string;
  sentDate: string;
  gapDays: number;
}) {
  return request<{ rows: FollowUpTask[] }>('/api/follow-ups/schedule', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function updateFollowUpStatus(id: string, status: FollowUpTaskStatus) {
  return request<{ id: string; status: FollowUpTaskStatus }>(`/api/follow-ups/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status })
  });
}

export function listCampaigns() {
  return request<{ rows: Campaign[] }>('/api/campaigns');
}

export function createCampaign(payload: { id?: string; name: string; description?: string }) {
  return request<Campaign>('/api/campaigns', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function listSuppression(q?: string) {
  return request<{ rows: SuppressedEmail[] }>(`/api/suppression${buildQuery({ q })}`);
}

export function listAuditLogs() {
  return request<{ rows: AuditLog[] }>('/api/audit');
}

export function listUsers() {
  return request<{ rows: AppUser[] }>('/api/users');
}

export function createUser(payload: {
  email: string;
  displayName: string;
  workspaceName?: string;
  role: UserRole;
  password: string;
}) {
  return request<AppUser>('/api/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function updateLeadStatus(id: string, status: LeadStatus) {
  return request<Lead>(`/api/leads/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status })
  });
}

export function updateLeadDetails(id: string, payload: LeadDetailsUpdate) {
  return request<Lead>(`/api/leads/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function bulkUpdateStatus(ids: string[], status: LeadStatus) {
  return request<{ updated: number }>('/api/leads/bulk/status', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, status })
  });
}

export function bulkUpdateFilteredStatus(filters: LeadFilters, status: LeadStatus) {
  return request<{ updated: number }>('/api/leads/filtered/status', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filters, status })
  });
}

export interface BulkLeadFieldUpdate {
  status?: LeadStatus;
  sentDate?: string | null;
  industry?: string | null;
  dataType?: LeadDataType | null;
  campaignId?: string | null;
  appendTag?: string | null;
}

export function bulkUpdateFields(ids: string[], updates: BulkLeadFieldUpdate) {
  return request<{ updated: number; job?: Job }>('/api/leads/bulk/fields', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, updates })
  });
}

export function bulkUpdateFilteredFields(filters: LeadFilters, updates: BulkLeadFieldUpdate) {
  return request<{ updated: number; job?: Job } | { queued: true; job: Job; targetCount: number }>('/api/leads/filtered/fields', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filters, updates })
  });
}

export function queueLeadExport(filters: LeadFilters) {
  return request<Job>('/api/exports/leads/jobs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filters })
  });
}

export function rollbackBulkJob(id: string) {
  return request<{ restored: number; available: boolean; reason: string | null }>(`/api/jobs/${id}/rollback`, {
    method: 'POST'
  });
}

export function uploadLeadImport(
  file: File,
  options: {
    defaultStatus: LeadStatus;
    campaignId?: string;
    dataType?: LeadDataType;
    defaultCountry?: string;
    defaultSentDate?: string;
    uploadDate?: string;
    sourceSheetName?: string;
    sheetPlans?: WorkbookSheetPlan[];
  }
) {
  const form = new FormData();
  form.append('file', file);
  form.append('defaultStatus', options.defaultStatus);
  if (options.sheetPlans) form.append('sheetPlans', JSON.stringify(options.sheetPlans));
  if (options.campaignId) {
    form.append('campaignId', options.campaignId);
  }
  if (options.dataType) {
    form.append('dataType', options.dataType);
  }
  if (options.defaultCountry) {
    form.append('defaultCountry', options.defaultCountry);
  }
  if (options.defaultSentDate) {
    form.append('defaultSentDate', options.defaultSentDate);
  }
  if (options.uploadDate) {
    form.append('uploadDate', options.uploadDate);
  }
  if (options.sourceSheetName) {
    form.append('sourceSheetName', options.sourceSheetName);
  }

  return request<Job>('/api/imports/leads', {
    method: 'POST',
    body: form
  });
}

export interface WorkbookSheetPlan {
  name: string;
  headerRow: number;
  columns: string[];
  defaultCountry?: string;
  defaultStatus?: LeadStatus;
  dataType?: LeadDataType;
  defaultSentDate?: string;
}
export interface WorkbookSheetPreview extends WorkbookSheetPlan {
  hidden: boolean;
  rowCount: number;
  previewRows: { number: number; values: string[] }[];
}
export async function previewWorkbook(file: File, signal?: AbortSignal, onProgress?: (status: string) => void) {
  const form = new FormData(); form.append('file', file);
  onProgress?.('Uploading workbook');
  const started = await request<{ previewId: string }>('/api/imports/workbook-preview', { method: 'POST', body: form, signal });
  while (!signal?.aborted) {
    const result = await request<{ status: string; sheets?: WorkbookSheetPreview[]; error?: string }>(`/api/imports/workbook-preview/${started.previewId}`, { signal });
    if (result.status === 'completed' && result.sheets) return { sheets: result.sheets };
    if (result.status === 'failed') throw new Error(result.error || 'Workbook preview failed.');
    onProgress?.(result.status === 'queued' ? 'Queued for preview' : 'Reading workbook');
    await new Promise(resolve => setTimeout(resolve, 2000));
  }
  throw new Error('Preview cancelled.');
}
export function getJobsBatch(ids: string[]) {
  return request<{ rows: Job[] }>('/api/jobs/batch', { method: 'POST', body: JSON.stringify({ ids }) });
}

export interface PastePayload {
  text: string;
  defaultStatus: LeadStatus;
  campaignId?: string;
  dataType?: LeadDataType;
  defaultCountry?: string;
  defaultSentDate?: string;
  uploadDate?: string;
  sourceSheetName?: string;
}

export function previewPastedLeads(payload: PastePayload) {
  return request<PastePreviewResult>('/api/imports/pasted-leads/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function pasteLeads(payload: PastePayload) {
  return request<PasteImportResult>('/api/imports/pasted-leads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function previewMediaExtraction(file: File, options: Omit<PastePayload, 'text'>) {
  const form = new FormData();
  form.append('file', file);
  form.append('defaultStatus', options.defaultStatus);
  if (options.campaignId) form.append('campaignId', options.campaignId);
  if (options.dataType) form.append('dataType', options.dataType);
  if (options.defaultCountry) form.append('defaultCountry', options.defaultCountry);
  if (options.defaultSentDate) form.append('defaultSentDate', options.defaultSentDate);
  if (options.uploadDate) form.append('uploadDate', options.uploadDate);
  if (options.sourceSheetName) form.append('sourceSheetName', options.sourceSheetName);
  return request<MediaExtractionPreview>('/api/imports/media-extractor/preview', {
    method: 'POST',
    body: form
  });
}

export function importExtractedLeads(payload: Omit<PastePayload, 'text'> & {
  rows: ExtractedLeadDraft[];
  sourceFileName?: string;
}) {
  return request<PasteImportResult>('/api/imports/media-extractor/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function previewDirectoryCrawl(payload: Omit<PastePayload, 'text'> & {
  url: string;
  maxPages?: number;
  followProfileLinks?: boolean;
}) {
  return request<MediaExtractionPreview>('/api/imports/directory-crawl/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function importDirectoryCrawl(payload: Omit<PastePayload, 'text'> & {
  rows: ExtractedLeadDraft[];
  sourceFileName?: string;
}) {
  return request<PasteImportResult>('/api/imports/directory-crawl/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function createSingleLead(payload: Omit<PastePayload, 'text'> & {
  lead: Partial<ExtractedLeadDraft> & { email: string };
  sourceFileName?: string;
}) {
  return request<PasteImportResult>('/api/imports/single-lead', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function pasteSuppressionLeads(payload: {
  text: string;
  status: 'negative_reply' | 'junk' | 'hard_bounce';
  sourceFileName?: string;
}) {
  return request<PasteImportResult>('/api/imports/negative-paste', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function uploadStatusCsv(
  file: File,
  options: {
    createMissing?: boolean;
    defaultStatus?: LeadStatus;
    allowFinalStatusOverwrite?: boolean;
    campaignId?: string;
    dataType?: LeadDataType;
    defaultCountry?: string;
    defaultSentDate?: string;
    uploadDate?: string;
    dateOrder?: 'auto' | 'dmy' | 'mdy';
  } = {}
) {
  const form = new FormData();
  form.append('file', file);
  if (options.createMissing) {
    form.append('createMissing', 'true');
  }
  if (options.defaultStatus) {
    form.append('defaultStatus', options.defaultStatus);
  }
  if (options.allowFinalStatusOverwrite) {
    form.append('allowFinalStatusOverwrite', 'true');
  }
  if (options.campaignId) {
    form.append('campaignId', options.campaignId);
  }
  if (options.dataType) {
    form.append('dataType', options.dataType);
  }
  if (options.defaultCountry) {
    form.append('defaultCountry', options.defaultCountry);
  }
  if (options.defaultSentDate) {
    form.append('defaultSentDate', options.defaultSentDate);
  }
  if (options.uploadDate) {
    form.append('uploadDate', options.uploadDate);
  }
  if (options.dateOrder && options.dateOrder !== 'auto') {
    form.append('dateOrder', options.dateOrder);
  }
  return request<Job>('/api/imports/status-updates', {
    method: 'POST',
    body: form
  });
}

export function uploadOutlookPst(
  file: File,
  options: {
    uploadDate?: string;
    defaultSentDate?: string;
  } = {}
) {
  const form = new FormData();
  form.append('file', file);
  if (options.uploadDate) {
    form.append('uploadDate', options.uploadDate);
  }
  if (options.defaultSentDate) {
    form.append('defaultSentDate', options.defaultSentDate);
  }
  return request<Job>('/api/imports/outlook-pst', {
    method: 'POST',
    body: form
  });
}

export function getOutlookPstParserStatus() {
  return request<{ installed: boolean; command: string | null; message?: string }>('/api/imports/outlook-pst/parser-status');
}

export function initOutlookPstChunkUpload(payload: {
  fileName: string;
  fileSize: number;
  chunkSize: number;
  uploadId?: string;
  uploadKey?: string;
  uploadDate?: string;
  defaultSentDate?: string;
}) {
  return request<{ uploadId: string; chunkCount: number; uploadedChunks: number[] }>('/api/imports/outlook-pst/chunks/init', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function uploadOutlookPstChunk(uploadId: string, index: number, chunk: Blob) {
  return request<{ ok: boolean; index: number; size: number }>(`/api/imports/outlook-pst/chunks/${uploadId}/${index}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: chunk
  });
}

export function completeOutlookPstChunkUpload(payload: {
  uploadId: string;
  fileName: string;
  uploadDate?: string;
  defaultSentDate?: string;
}) {
  return request<Job>('/api/imports/outlook-pst/chunks/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}

export function uploadDeduplication(file: File, options: { windowMonths: number | 'all' }) {
  const form = new FormData();
  form.append('file', file);
  if (options.windowMonths === 'all') {
    form.append('allTime', 'true');
    form.append('windowMonths', 'all');
  } else {
    form.append('windowMonths', String(options.windowMonths));
  }

  return request<Job>('/api/imports/deduplicate', {
    method: 'POST',
    body: form
  });
}

export function getJob(id: string) {
  return request<Job>(`/api/jobs/${id}`);
}

export function pauseEnrichmentJob(id: string) {
  return request<{ ok: boolean }>(`/api/jobs/${id}/enrichment/pause`, { method: 'POST' });
}

export function resumeEnrichmentJob(id: string) {
  return request<{ ok: boolean }>(`/api/jobs/${id}/enrichment/resume`, { method: 'POST' });
}

export function retryFailedEnrichmentJob(id: string) {
  return request<Job>(`/api/jobs/${id}/enrichment/retry-failed`, { method: 'POST' });
}

export function exportPath(filters: LeadFilters, format: 'csv' | 'xlsx' = 'csv') {
  return `/api/exports/leads${buildQuery({
    ...filters,
    format,
    status: filters.status || undefined,
    preset: filters.preset || undefined
  })}`;
}

function filenameFromDisposition(disposition: string | null, fallback: string) {
  const match = disposition?.match(/filename="?([^"]+)"?/i);
  return match?.[1] ?? fallback;
}

export async function downloadFile(path: string, fallbackName = 'download.csv') {
  const response = await fetch(`${API_BASE}${path}`, withAuth());
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.message ?? `Download failed with ${response.status}`);
  }
  if (response.status === 202) {
    const queued=await response.json() as {jobId:string;downloadPath:string};
    for (;;) {
      await new Promise(resolve=>setTimeout(resolve,3000));
      const job=await getJob(queued.jobId);
      if (job.status==='failed') throw new Error(job.errorMessage || 'Export failed.');
      if (job.status==='completed') return downloadFile(queued.downloadPath,fallbackName);
    }
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filenameFromDisposition(response.headers.get('Content-Disposition'), fallbackName);
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}
